import { logger } from 'common/services/logger';
import { assert, generateDynamicPath } from 'common/utils';
import { communityActions } from 'community/api/actions';
import { invalidateMyCommunitiesEffect } from 'community/api/effects';
import { selectMyCommunitiesIds, selectMyCommunity } from 'community/api/selectors';
import { isAccinCommunity } from 'community/utils/isAccinCommunity';
import i18n from 'i18n';
import { addAppErrorMessage, addAppInfoMessage } from 'modules/alerts/state/actions';
import { natalieFeedbackApi } from 'natalie/api/feedbackApi';
import { natalieEventApi } from 'natalie/api/natalieEventApi';
import { selectCurrentNatalieEvent } from 'natalie/api/natalieEventApi.selectors';
import { NATALIE_VERSION } from 'natalie/constants';
import { NatalieRoutes, getAccountlessLandingUrl, getNataliePublicPath } from 'pages/Natalie/routes';
import { push } from 'redux-first-history';
import { selectUserAccount } from 'user/api/selectors';

import type { natalieActions } from './natalie.actions';
import type { RegisterToNatalieEventArgs } from 'natalie/api/natalieEvent.types';
import type { PostNatalieFeedbackPayload } from 'natalie/api/types';
import type { Effect } from 'store';

export const startChattingButtonClickedEffect: Effect<
  ReturnType<typeof natalieActions.detailsPage.startChattingButton.clicked>
> = ({ payload: { id } }, { dispatch }) => {
  dispatch(natalieEventApi.endpoints.registerToNatalieEvent.initiate({ id }));
};

export const joinButtonClickedEffect: Effect = ({ payload: { id } }, { dispatch, getState }) => {
  try {
    const state = getState();
    const natalieEvent = selectCurrentNatalieEvent(state);

    assert(!!natalieEvent, 'Natalie event is not present');

    const hasVideo = !!natalieEvent.videoUrl;
    const searchParams = window.location.search;
    const urlParams = new URLSearchParams(searchParams);
    const wantsConfigure = urlParams.get('configure') === 'true';

    const roomRoute = wantsConfigure ? NatalieRoutes.Configure : NatalieRoutes.RoomLiveKit;
    const route = hasVideo ? NatalieRoutes.WaitingRoom : roomRoute;

    // Preserve query params for the navigation
    const path = generateDynamicPath(route, { id }) + searchParams;
    dispatch(push(path, { allow: true }));
  } catch (error) {
    logger.error(error);
  }
};

export const copyPublicLinkButtonClickedEffect: Effect = async ({ payload }, { dispatch }) => {
  try {
    const url = getNataliePublicPath(payload);

    await navigator.clipboard.writeText(url);

    dispatch(addAppInfoMessage({ message: i18n.t('clipboard.copiedToClipboard'), timeout: 2_000 }));
  } catch (_error: unknown) {
    dispatch(addAppErrorMessage({ message: i18n.t('clipboard.copyToClipboardFailed'), timeout: 3_000 }));
  }
};

export const copyAccountlessLinkButtonClickedEffect: Effect<
  ReturnType<typeof natalieActions.list.moreActions.copyAccountlessLinkButtonClicked>
> = async ({ payload: { eventId, inviteCode } }, { dispatch }) => {
  try {
    const url = getAccountlessLandingUrl(eventId, inviteCode);

    await navigator.clipboard.writeText(url);

    dispatch(addAppInfoMessage({ message: i18n.t('clipboard.copiedToClipboard'), timeout: 2_000 }));
  } catch (_error: unknown) {
    dispatch(addAppErrorMessage({ message: i18n.t('clipboard.copyToClipboardFailed'), timeout: 3_000 }));
  }
};

const updateMyCommunities: Effect = async ({ meta }, { dispatch, getState }) => {
  const state = getState();
  const args = meta.arg.originalArgs as RegisterToNatalieEventArgs;

  const myCommunitiesIds = selectMyCommunitiesIds(state);
  const { communityId } = await dispatch(natalieEventApi.endpoints.getNatalieEvent.initiate(args.id)).unwrap();

  if (communityId !== undefined && !myCommunitiesIds?.includes(Number(communityId))) {
    // user was not in open natter community before, but submitting availability means they were added to the community
    // so we want to refresh community related data
    dispatch(
      communityActions.community.registeredToCommunity({
        communityId: Number(communityId),
        showMemberSetupDialog: false,
      })
    );
  }
};

const navigateToJoin: Effect = ({ meta }, { dispatch, getState }) => {
  const args = meta.arg.originalArgs as RegisterToNatalieEventArgs;
  // Preserve query params (including provider) through navigation
  const searchParams = window.location.search;

  const state = getState();
  const natalieEvent = selectCurrentNatalieEvent(state);

  const route =
    natalieEvent && isAccinCommunity(natalieEvent.communityId) ? NatalieRoutes.BeforeJoin : NatalieRoutes.Join;

  dispatch(push(generateDynamicPath(route, { id: args.id }) + searchParams, { allow: true }));
};

export const registerToNatalieEventSuccessEffect: Effect = async (action, api) => {
  const args = action.meta.arg.originalArgs as RegisterToNatalieEventArgs;

  if (args.silent) {
    // Silent registration (magic link flow): only update community membership, no navigation (fire-and-forget)
    try {
      await updateMyCommunities(action, api);
    } catch (error: unknown) {
      logger.error(error);
    }
  } else {
    // Invalidate communities cache (fire-and-forget, doesn't need to block navigation)
    invalidateMyCommunitiesEffect(action, api);
    // Wait for community switch to complete before navigating — the join page
    // and member setup dialog need the active community to be set correctly
    try {
      await updateMyCommunities(action, api);
    } catch (error: unknown) {
      logger.error(error);
    }
    navigateToJoin(action, api);
  }
};

export const beforeJoinContinueButtonClickedEffect: Effect<
  ReturnType<typeof natalieActions.beforeJoinPage.continueButton.clicked>
> = ({ payload: { id } }, { dispatch }) => {
  const searchParams = window.location.search;
  dispatch(push(generateDynamicPath(NatalieRoutes.Join, { id }) + searchParams, { allow: true }));
};

export const natalieIssueReportedEffect: Effect = ({ payload: { type, description } }, { dispatch, getState }) => {
  const state = getState();
  const user = selectUserAccount(state);
  const community = selectMyCommunity(state);
  const natalieEvent = selectCurrentNatalieEvent(state);

  try {
    assert(!!user, 'User is not present');
    assert(!!community, 'Community is not present');

    const { id: userId, firstName, lastName, email } = user;
    const { id: communityId, name } = community;

    const payload: PostNatalieFeedbackPayload = {
      type,
      description,
      user: {
        id: userId,
        name: `${firstName} ${lastName}`,
        email,
      },
      community: {
        id: communityId,
        name,
      },
      event: natalieEvent ? { id: natalieEvent.id, name: natalieEvent.name } : null,
      agoraChannel: '',
      natalieVersion: NATALIE_VERSION,
      timestamp: new Date().toISOString(),
    };

    dispatch(natalieFeedbackApi.endpoints.postFeedback.initiate(payload, { track: false }));
  } catch (error) {
    logger.error(error);
    dispatch(addAppErrorMessage(i18n.t('natalie:feedback.failedToReportAnIssue')));
  }
};
